import {
  ApiResponse,
  CallAPIReturn,
  NalogRequestData,
  RequestData
} from '../types/types';
import { NALOG_API, PUBLIC_API } from '../constants/config';
import { version } from './version';

type CallOptions<T> = {
  fallback?: T;
  timeoutMs?: number;
  onError?: (info: { message: string; status?: number; raw?: unknown }) => void;
};

export const callNalogAPI = async <T = CallAPIReturn>(
  method: RequestInit['method'],
  url: string,
  data?: NalogRequestData,
  options?: CallOptions<T>
): Promise<T> => {
  const { fallback, timeoutMs = 15000, onError } = options ?? {};
  const config: RequestInit = {
    method,
    headers: {
      Accept: 'application/json, text/plain, */*',
      'Content-Type': 'application/json'
    },
    body: data ? JSON.stringify(data) : undefined
  };

  const fetchTarget = `${NALOG_API}${url}`;

  // timeout preko AbortController-a
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  config.signal = controller.signal;

  try {
    const res = await fetch(fetchTarget, config);

    // 204 No Content – vrati undefined kao T
    if (res.status === 204) {
      return undefined as unknown as T;
    }

    const ct = res.headers.get('content-type') || '';
    const isJson = ct.includes('application/json');

    // bezbedan parse (nikad ne baca)
    let payload: unknown;
    if (isJson) {
      try {
        payload = await res.json();
      } catch {
        payload = null;
      }
    } else {
      try {
        payload = await res.text();
      } catch {
        payload = '';
      }
    }

    if (!res.ok) {
      const msg =
        (payload &&
          typeof payload === 'object' &&
          'message' in (payload as Record<string, unknown>) &&
          typeof (payload as Record<string, unknown>).message === 'string' &&
          (payload as Record<string, unknown>).message) ||
        (typeof payload === 'string' && payload) ||
        `HTTP ${res.status}`;

      onError?.({ message: String(msg), status: res.status, raw: payload });
      return (fallback as T) ?? (null as unknown as T);
    }

    return payload as T;
  } catch (err) {
    const message =
      err instanceof Error
        ? err.name === 'AbortError'
          ? 'Zahtev je istekao (timeout).'
          : err.message
        : 'Mrežna greška';

    onError?.({ message });
    return (fallback as T) ?? (null as unknown as T);
  } finally {
    clearTimeout(timer);
  }
};

export const callPublicAPI = async <T = CallAPIReturn>(
  method: RequestInit['method'],
  url: string,
  params?: URLSearchParams | null,
  data?: RequestData,
  signal?: AbortSignal,
  additionalHeaders?: Record<string, string>,
  timeoutMs = 15000
): Promise<ApiResponse<T>> => {
  const headers: Record<string, string> = {
    Accept: 'application/json, text/plain, */*',
    'Content-Type': 'application/json',
    'X-Version': version,
    ...(additionalHeaders ?? {})
  };

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  if (signal) {
    signal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  const config: RequestInit = {
    method,
    credentials: 'include',
    headers,
    signal: controller.signal,
    ...(data && { body: JSON.stringify(data) })
  };

  const query = params ? `?${params.toString()}` : '';
  const fetchTarget = `${PUBLIC_API}${url}${query}`;

  try {
    const res = await fetch(fetchTarget, config);
    return (await res.json()) as ApiResponse<T>;
  } finally {
    clearTimeout(timer);
  }
};
